import { Participant, SpeakerRole } from "@/types/meeting";
import { getPrimaryEmail } from "@/lib/clientContact";

export const getParticipantName = (
  participant?: Partial<Participant> | null,
): string => {
  if (!participant) return "";

  const client = participant.client;
  if (client) {
    const clientName =
      `${client.firstname ?? ""} ${client.lastname ?? ""}`.trim();
    if (clientName) return clientName;
  }

  const user = participant.user;
  if (user) {
    const userName = `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();
    if (userName) return userName;
  }

  // Fall back to stored name (covers the just-created frame before client/user is hydrated)
  return participant.name ?? "";
};

const participantRoleLabels: Record<SpeakerRole, string> = {
  CLIENT: "Client",
  ADVISOR: "Advisor",
  ASSISTANT: "Assistant",
  LEGAL_ADVISOR: "Legal advisor",
  INSURANCE_ADVISOR: "Insurance advisor",
  TAX_ADVISOR: "Tax advisor",
  OTHER: "Other",
  UNKNOWN: "",
};

export const getParticipantRoleLabel = (
  participant?: Partial<Participant> | null,
): string => {
  const detectedRole =
    participant?.role && participantRoleLabels[participant.role];
  if (detectedRole) return detectedRole;
  if (participant?.userId) return "Advisor";
  if (participant?.clientId) return "Client";
  return "";
};

/**
 * The email to display for a participant.
 *
 * Prefers the participant's own `email`, which the backend populates for
 * advisors and CRM-synced clients. For clients created through the legacy flow
 * that field is null, so fall back to the linked client's primary email. This
 * mirrors how {@link getParticipantName} resolves through the nested client.
 */
export const getParticipantEmail = (
  participant?: Partial<Participant> | null,
): string => {
  if (!participant) return "";

  if (participant.email) return participant.email;

  if (participant.user?.email) return participant.user.email;

  if (participant.client) return getPrimaryEmail(participant.client);

  return "";
};

/**
 * The clients a follow-up email should be pre-addressed to: the meeting's
 * clients with a resolvable email, excluding the logged-in advisor (so we never
 * self-address) and non-client participants (other users, unlinked speakers).
 * Reads from the live participants list, so removing a client drops them here
 * too. May be empty — "Open in mail" still opens a draft for the advisor to
 * address by hand. Order is preserved; callers split it into to/cc.
 */
export const getClientEmailRecipients = (
  participants: Participant[] | null | undefined,
  currentUserId?: string,
): Participant[] =>
  (participants ?? []).filter(
    (p) =>
      !!p.clientId &&
      (!currentUserId || p.userId !== currentUserId) &&
      !!getParticipantEmail(p),
  );
